import { Ecc, QrCode, QrSegment } from '@rc-component/qrcode/es/libs/qrcodegen';
import { isNumberedTableName, squish } from '../../../services/helpers/OnSiteOrder';

/**
 * The QR code image Download QR code saves (PROD-724): a 1200 × 1500 px PNG (10 × 12.5 cm at
 * 300 dpi), white, with the restaurant's name, the QR code, "Table" for names that start with a
 * number, and the table's name. No FoodAmigos branding. See tests/components/takeout/tables/tableQr.test.js.
 */
export const QR_IMAGE = Object.freeze({
  width: 1200,
  height: 1500,
  /** The box the QR code is drawn in, quiet zone included; the code itself may be a little smaller. */
  qrSize: 900,
  qrTop: 220,
  restaurantY: 150,
  restaurantLinesY: [110, 176],
  labelY: 1250,
  nameY: 1390,
  nameLinesY: [1340, 1440],
  maxTextWidth: 1080,
});

/** Error correction M and the 4-module quiet zone the QR code standard asks for. */
export const QR_QUIET_ZONE = 4;

/** A long name is drawn smaller; a name that needs two lines always is. */
const LONG_NAME_FROM = 15;
const NAME_SIZE = 128;
const LONG_NAME_SIZE = 88;
const RESTAURANT_FONT = '600 56px';
const LABEL_FONT = '500 48px';

/** The fonts the image draws with, to load before it is drawn. */
export const QR_IMAGE_FONTS = [RESTAURANT_FONT, LABEL_FONT, `700 ${NAME_SIZE}px`, `700 ${LONG_NAME_SIZE}px`];

/** "Table" above the name only for a name that starts with a number: "Terrace 1" shows alone. */
export function tableQrTypography(name) {
  const clean = squish(name);

  return {
    showLabel: isNumberedTableName(clean),
    nameSize: clean.length >= LONG_NAME_FROM ? LONG_NAME_SIZE : NAME_SIZE,
  };
}

/**
 * `table-3-qr.png`, `table-terrace-1-qr.png`; letters with accents lose them. A name with no Latin
 * letter or digit ("Терраса 1") is told apart by the table's id: `table-12-qr.png`.
 */
export function tableQrFileName(name, id = null) {
  const slug = squish(name)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (slug) {
    return `table-${slug}-qr.png`;
  }

  return id != null ? `table-${id}-qr.png` : 'table-qr.png';
}

/** The QR code's modules for a link (`true` is dark), at error correction M exactly. */
export function qrModules(link) {
  return QrCode.encodeSegments(QrSegment.makeSegments(String(link ?? '')), Ecc.MEDIUM, 1, 40, -1, false).getModules();
}

/**
 * Where the QR code goes: a whole number of pixels per module, as many as fit the 900 px box with
 * the quiet zone, centred in it. Whole pixels keep every module the same size and its edges sharp,
 * whatever the module count (a longer link, e.g. on a partner's domain, has more modules).
 */
export function qrGeometry(moduleCount) {
  const cells = moduleCount + QR_QUIET_ZONE * 2;
  const moduleSize = Math.max(1, Math.floor(QR_IMAGE.qrSize / cells));
  const size = cells * moduleSize;

  return {
    moduleSize,
    size,
    left: Math.floor((QR_IMAGE.width - size) / 2),
    top: QR_IMAGE.qrTop + Math.floor((QR_IMAGE.qrSize - size) / 2),
  };
}

/**
 * The text in at most `maxLines` lines no wider than `maxWidth` (`measure(text)` gives a width),
 * broken between words, or inside a word too long for a line. What doesn't fit ends the last line
 * with "…". A name is never squeezed sideways.
 */
export function fitTextLines(measure, text, maxWidth, maxLines = 2) {
  const words = squish(text).split(' ').filter(Boolean);
  const lines = [];
  let current = '';

  while (words.length) {
    const candidate = current ? `${current} ${words[0]}` : words[0];

    if (measure(candidate) <= maxWidth) {
      current = candidate;
      words.shift();
    } else if (lines.length === maxLines - 1) {
      break;
    } else if (current) {
      lines.push(current);
      current = '';
    } else {
      // One word wider than a line: as many characters as fit, the rest on the next line.
      const chars = [...words[0]];
      let fit = chars.length - 1;

      while (fit > 1 && measure(chars.slice(0, fit).join('')) > maxWidth) {
        fit -= 1;
      }

      fit = Math.max(1, fit);
      lines.push(chars.slice(0, fit).join(''));
      words[0] = chars.slice(fit).join('');

      if (!words[0]) {
        words.shift();
      }
    }
  }

  if (!words.length) {
    return current ? [...lines, current] : lines;
  }

  const rest = [...[current, ...words].filter(Boolean).join(' ')];

  while (rest.length && measure(`${rest.join('').trimEnd()}…`) > maxWidth) {
    rest.pop();
  }

  return [...lines, `${rest.join('').trimEnd()}…`];
}

/** Fills the dark modules, a row's adjacent ones as one rectangle, at whole pixels. */
function drawModules(ctx, modules) {
  const { moduleSize, left, top } = qrGeometry(modules.length);
  const offset = QR_QUIET_ZONE * moduleSize;

  modules.forEach((row, y) => {
    let x = 0;

    while (x < row.length) {
      if (!row[x]) {
        x += 1;
      } else {
        const start = x;

        while (x < row.length && row[x]) {
          x += 1;
        }

        ctx.fillRect(
          left + offset + start * moduleSize,
          top + offset + y * moduleSize,
          (x - start) * moduleSize,
          moduleSize,
        );
      }
    }
  });
}

/**
 * Draws the image onto a 2D context: the QR code from its module matrix (`modules`, from
 * `qrModules`), the names on one line, or two when they don't fit.
 */
export function drawTableQrPoster(ctx, { modules, restaurantName, tableName, tableLabel, fontFamily }) {
  const { width, height, restaurantY, restaurantLinesY, labelY, nameY, nameLinesY, maxTextWidth } = QR_IMAGE;
  const { showLabel, nameSize } = tableQrTypography(tableName);
  const centre = width / 2;
  const measure = (text) => ctx.measureText(text).width;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = '#000000';
  ctx.font = `${RESTAURANT_FONT} ${fontFamily}`;
  const restaurantLines = fitTextLines(measure, restaurantName, maxTextWidth);
  restaurantLines.forEach((line, index) =>
    ctx.fillText(line, centre, restaurantLines.length > 1 ? restaurantLinesY[index] : restaurantY),
  );

  drawModules(ctx, modules);

  if (showLabel) {
    ctx.fillStyle = '#555555';
    ctx.font = `${LABEL_FONT} ${fontFamily}`;
    ctx.fillText(tableLabel, centre, labelY);
  }

  ctx.fillStyle = '#000000';
  ctx.font = `700 ${nameSize}px ${fontFamily}`;
  let nameLines = fitTextLines(measure, tableName, maxTextWidth, 1);

  // Too wide for one line: the smaller size, on two lines.
  if (nameLines.length && nameLines[0] !== squish(tableName)) {
    ctx.font = `700 ${LONG_NAME_SIZE}px ${fontFamily}`;
    nameLines = fitTextLines(measure, tableName, maxTextWidth, 2);
  }

  nameLines.forEach((line, index) => ctx.fillText(line, centre, nameLines.length > 1 ? nameLinesY[index] : nameY));
}
